<template>
    <Modal class="adjust-float-modal"
           size="lg"
           :title="titleKey"
           :icon="icon"
           :cancel="cancel"
           :ok="ok">
        <form class="form-v cols">
            <div class="cols__col--1-2">
                <div class="form-group">
                    <label class="control-label">{{ l(labelKey) }}</label>
                    <NumPad v-model="amount"
                            :show-value-as-currency="true"
                            :show-sign-button="false"
                            :show-cash-buttons="true"
                            @ok="ok" />
                </div>
            </div>
            <div class="cols__col--1-2">
                <div class="form-group">
                    <label for="adjust-float__reason"
                           class="control-label">
                        {{ l('ADJUST_FLOAT.REASON') }}
                    </label>
                    <select id="adjust-float__reason"
                            v-model="selectedReason"
                            name="reason"
                            :readonly="selectableReasons.length < 2"
                            class="form-control input-lg">
                        <option v-for="reason in selectableReasons"
                                :key="reason.ID"
                                :value="reason">
                            {{ reason.reason }}
                        </option>
                    </select>
                </div>

                <div class="form-group">
                    <label class="control-label"
                           for="adjust-float__narrative">
                        {{ l('ADJUST_FLOAT.NARRATIVE') }}
                    </label>
                    <textarea id="adjust-float__narrative"
                              v-model="narrative"
                              name="narrative"
                              rows="6"
                              class="form-control input-lg"
                              :required="selectedReason.narrative"></textarea>
                </div>

                <ErrorMessage v-if="error"
                              class="mt-1"
                              :error="error"></ErrorMessage>
            </div>
        </form>
    </Modal>
</template>

<script>
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import FloatAdjustment from '@/models/FloatAdjustment';
import Modal from '@/vue/components/Modals/Modal';
import NumPad from '@/vue/components/NumPad/NumPad';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'AdjustFloatModal',

    components: {
        ErrorMessage,
        NumPad,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        mode: {
            type: String,
            default: 'add',
        },

        templateText: {
            type: Object,
            default: () => ({}),
        },

        /**
         * The reasons that can be selected can be passed in,
         * otherwise they will be loaded from the company's list of possible reasons.
         */
        reasons: {
            type: Array,
            default: null,
        },
    },

    data() {
        return {
            /**
             * @type {FloatAdjustmentReason[]|null}
             */
            floatAdjustmentReasons: null,

            /**
             * @type {FloatAdjustmentReason}
             */
            selectedReason: null,

            /**
             * @type {number}
             */
            amount: 0.00,

            /**
             * @type {string}
             */
            narrative: '',

            /**
             * @type {Error|null}
             */
            error: null,
        };
    },

    computed: {
        isAdd() {
            return this.mode === 'add';
        },

        titleKey() {
            switch (this.mode) {
                case 'z-read':
                    return 'REPORTS.Z_READ_PROMPT_FLOAT_ADJUSTMENT_TITLE';

                case 'remove':
                    return 'ADJUST_FLOAT.REMOVE_CASH_FROM_TILL';

                case 'add':
                default:
                    return 'ADJUST_FLOAT.ADD_CASH_TO_TILL';
            }
        },

        labelKey() {
            switch (this.mode) {
                case 'remove':
                case 'z-read':
                    return 'ADJUST_FLOAT.AMOUNT_REMOVED';

                case 'add':
                default:
                    return 'ADJUST_FLOAT.AMOUNT_TO_ADD';
            }
        },

        icon() {
            switch (this.mode) {
                case 'remove':
                case 'z-read':
                    return 'fa-money-from-bracket';

                case 'add':
                default:
                    return 'fa-money-simple-from-bracket';
            }
        },

        selectableReasons() {
            if (this.reasons !== null) {
                return this.reasons;
            }

            return container.userService.company.floatAdjustmentReasons.filter(
                (r) => r.withdrawal === !this.isAdd,
            );
        },
    },

    watch: {
        selectableReasons: {
            handler() {
                if (this.selectedReason === null || !this.selectableReasons.includes(this.selectedReason)) {
                    // Select the first reason by default.
                    this.selectedReason = this.selectableReasons[0];
                }
            },
            immediate: true,
        },
    },

    methods: {
        ok() {
            this.error = null;
            try {
                const floatAdjustment = new FloatAdjustment(
                    this.selectedReason,
                    this.amount,
                    this.narrative,
                );

                this.resolve({ floatAdjustment });
            } catch (e) {
                this.error = e;
            }
        },
    },
};
</script>
